import { useReducer } from 'react';
import { useActionDispatchContextState } from 'lib/ActionDispatchContext';

import {
  ActionType,
  requestActionCreator,
  successActionCreator,
  failureActionCreator,
} from '../actionCreators';
import { successToast } from 'lib/components/toasts/success';
import { VideoTranscriptionResponse, startTranscription } from 'lib/api';

interface VideoTranscription {
  data: VideoTranscriptionResponse;
  loading: boolean;
  error: string | null;
  start: (videoId: string) => void;
}

interface VideoTranscriptionStoreState {
  data: VideoTranscriptionResponse | undefined;
  error: string | null;
  loading: boolean;
  isValid: boolean;
}

function reducer(state: VideoTranscriptionStoreState, action: ActionType) {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        data: action.payload,
        loading: false,
        error: null,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        data: undefined,
        loading: false,
        error: action.payload,
      };

    default:
      return state;
  }
}

export const useVideoTranscriptionStart = (): VideoTranscription => {
  const { setAction } = useActionDispatchContextState();

  const initialState = {
    data: undefined,
    loading: false,
    error: null,
    isValid: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const start = async (videoId: string) => {
    dispatch(
      requestActionCreator({
        type: 'FETCH_REQUEST',
      })
    );

    try {
      const response: VideoTranscriptionResponse =
        await startTranscription(videoId);

      dispatch(
        successActionCreator({
          type: 'FETCH_SUCCESS',
          payload: response,
        })
      );

      successToast({ title: 'Transcription Started' });

      return {
        data,
        loading: false,
        error: null,
      };
    } catch (err) {
      // Axios automatically throws an error in case of HTTP or Network errors
      // so we can eventually put error parsing here and return whatever value we want based on that.
      const errorMessage =
        'An error occurred while starting transcription. Please try again later.';
      dispatch(
        failureActionCreator({
          type: 'FETCH_FAILURE',
          payload: errorMessage,
        })
      );

      setAction({ type: 'START_VIDEO_TRANSCRIPTION' });

      return Promise.reject({
        loading: false,
        error: errorMessage,
      });
    }
  };

  const { data, error, loading } = state;

  return {
    start,
    data,
    loading,
    error,
  };
};
